import { useEffect, useMemo, useState } from 'react';
import { getApiErrorMessage, paymentAPI, type DeliveryBoardOrderDto } from '@matix/api-client';
import type { PaymentType } from '@matix/types';
import { roundMoney } from '@matix/utils';
import { toast } from '../../store/toast';
import { useLang } from '../../lib/language';
import { amountStep, moneyEpsilon, moneyPrecision } from '../../store/currency';
import { useCurrency } from '../print/useCurrency';
import { customerOf, orderNo, pick } from './model';

/**
 * The legacy `payReceiptModal`: "Delivered" on an order whose money has not
 * been taken opens this first. Same tenders the legacy page offered — every
 * payment type with `Code > 0` except Credit (2) — split across as many as the
 * driver brought back, each at most once.
 *
 * The payment and the "Delivered" are ONE request and one transaction on the
 * server, so an order can no longer end up paid-but-not-delivered (or the
 * reverse), which the legacy two-call version could.
 */
export function DeliveryPayDialog({
  order,
  busy,
  onConfirm,
  onClose,
}: {
  order: DeliveryBoardOrderDto;
  busy: boolean;
  onConfirm: (payments: Array<{ payment_type_id: number; amount: number }>) => void;
  onClose: () => void;
}): JSX.Element {
  const { t, lang } = useLang();
  const currency = useCurrency();
  const precision = moneyPrecision();
  const round = (n: number) => roundMoney(n, precision);
  const money = (n: number) =>
    `${(Number.isFinite(n) ? n : 0).toFixed(Math.max(0, Math.min(8, precision)))} ${currency.code}`;

  const [types, setTypes] = useState<PaymentType[]>([]);
  // Raw strings while typing, so "0." does not eat its own decimal point.
  const [splits, setSplits] = useState<Array<{ payment_type_id: number; amount: string }>>([]);
  const owed = round(order.unpaid_amount);

  useEffect(() => {
    let cancelled = false;
    paymentAPI
      .listTypes()
      .then((res) => {
        if (!cancelled) setTypes(res.payment_types.filter((p) => p.code > 0 && p.code !== 2));
      })
      .catch((err) => toast(getApiErrorMessage(err), 'error'));
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      cancelled = true;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const payload = useMemo(
    () => splits.map((s) => ({ payment_type_id: s.payment_type_id, amount: round(Number(s.amount) || 0) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [splits, precision],
  );
  const paid = payload.reduce((s, p) => s + p.amount, 0);
  const remaining = round(owed - paid);
  const balanced = splits.length > 0 && payload.every((p) => p.amount > 0) && Math.abs(remaining) <= moneyEpsilon();
  const over = remaining < -moneyEpsilon();

  function add(type: PaymentType): void {
    if (splits.some((s) => s.payment_type_id === type.id)) return;
    const amount = round(Math.max(0, remaining));
    if (amount <= 0) return;
    setSplits((prev) => [...prev, { payment_type_id: type.id, amount: String(amount) }]);
  }

  return (
    <div className="cc-modal-backdrop" role="dialog" aria-modal="true" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="cc-modal dm-modal dm-pay">
        <header className="cc-modal-head">
          <div>
            <div className="cc-modal-title">{t({ en: 'Payment', ar: 'الدفع' })}</div>
            <div className="cc-modal-sub">
              {orderNo(order)} · {customerOf(order, lang) || t({ en: 'Walk-in', ar: 'عميل نقدي' })}
              {order.driver_name ? ` · ${pick(lang, order.driver_name, order.driver_name_ar)}` : ''}
            </div>
          </div>
          <button type="button" className="cc-drawer-close" onClick={onClose} aria-label={t({ en: 'Close', ar: 'إغلاق' })}>
            ×
          </button>
        </header>

        <div className="dm-pay-hero">
          <span>{t({ en: 'Unpaid', ar: 'غير مدفوع' })}</span>
          <strong>{money(owed)}</strong>
        </div>

        <div className="cc-settle-methods">
          {types.map((p) => {
            const used = splits.some((s) => s.payment_type_id === p.id);
            return (
              <button
                key={p.id}
                type="button"
                className={`cc-settle-method ${p.code === 1 ? 'cc-settle-method--cash' : ''}`}
                disabled={used || remaining <= moneyEpsilon()}
                onClick={() => add(p)}
              >
                {pick(lang, p.name, p.name_ar)}
              </button>
            );
          })}
        </div>

        {splits.length > 0 && (
          <ul className="cc-settle-splits">
            {splits.map((s, i) => {
              const type = types.find((p) => p.id === s.payment_type_id);
              return (
                <li key={s.payment_type_id}>
                  <span>{type ? pick(lang, type.name, type.name_ar) : '—'}</span>
                  <input
                    type="number"
                    inputMode="decimal"
                    min={0}
                    step={amountStep()}
                    value={s.amount}
                    onChange={(e) =>
                      setSplits((prev) => prev.map((x, ix) => (ix === i ? { ...x, amount: e.target.value } : x)))
                    }
                  />
                  <button
                    type="button"
                    onClick={() => setSplits((prev) => prev.filter((_, ix) => ix !== i))}
                    title={t({ en: 'Remove', ar: 'حذف' })}
                  >
                    ×
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        <div className={`cc-settle-balance ${balanced ? 'cc-settle-balance--ok' : ''}`}>
          <span>
            {over
              ? t({ en: 'Paid amount must not be greater than the unpaid amount', ar: 'المبلغ المدفوع يجب ألا يزيد عن المبلغ غير المدفوع' })
              : t({ en: 'Remaining', ar: 'المتبقي' })}
          </span>
          <strong>{money(remaining)}</strong>
        </div>

        <div className="dm-modal-actions">
          <button type="button" className="cc-btn" onClick={onClose}>
            {t({ en: 'Close', ar: 'إغلاق' })}
          </button>
          <button
            type="button"
            className="cc-btn cc-btn--primary"
            disabled={!balanced || busy}
            onClick={() => onConfirm(payload)}
          >
            {busy ? t({ en: 'Posting…', ar: 'جارٍ الترحيل…' }) : t({ en: 'Pay & mark delivered', ar: 'دفع وتسليم' })}
          </button>
        </div>
      </div>
    </div>
  );
}
